// Décorations : couche COSMÉTIQUE d'un niveau, JAMAIS dans la simulation ni le hash (comme le
// monde). Une 2e grille 28×16 par-dessus le terrain ; le moteur l'ignore, le client la dessine.
// Module PARTAGÉ (client + serveur), aucune dépendance DOM ni moteur.
import { MAX_COLS, MAX_ROWS } from './types'
import type { WorldKey } from './worlds'

export type DecoKind =
  | 'torche' | 'plante' | 'banniere' | 'cristal' | 'tonneau' | 'crane' | 'panneau' | 'chaine'
  | 'cactus' | 'bonhomme' | 'valve' | 'champignon' | 'fleurs' | 'toile' | 'bougie'
  | 'herbe' | 'buisson' | 'rocher' | 'souche' | 'lierre' | 'stalagmite' | 'pierres'
  | 'lanterne' | 'fanion' | 'lucioles' | 'barriere' | 'fleche' | 'cascadeEau' | 'cascadeLave'
  | 'corail' | 'tombe'
  | 'tresorDiamant' | 'tresorRubis' | 'tresorEmeraude' | 'tresorSaphir' | 'tresorAmethyste'
  | 'tresorCouronne' | 'tresorSceptre' | 'tresorCalice' | 'tresorAnneau' | 'tresorAmulette'
  | 'tresorOeuf' | 'tresorLingot' | 'tresorSac' | 'tresorIdole' | 'tresorMasque'
  | 'tresorScarabee' | 'tresorPerle' | 'tresorCristalPolaire' | 'tresorRouage' | 'tresorGrimoire'
  | 'hieroglyphes' | 'cartouche' | 'oeilHorus'

export interface DecoMeta {
  /** Libellé français (palette de l'éditeur). */
  name: string
  /** Caractère d'éditeur, UNIQUE, hors caractères de terrain/objets (grille `decor`). */
  ch: string
  /** Code numérique 1..N stocké dans la grille runtime (0 = case vide). */
  code: number
  /** Décoration animée au tick (flamme, lueur, aiguille) — overlay procédural au rendu. */
  animated: boolean
  /** Biome de rattachement dans la palette (`'all'` = socle universel). */
  biome: 'all' | WorldKey
  /** PREMIER PLAN : dessinée PAR-DESSUS les entités, en semi-transparence (cascades : le joueur
   *  passe DERRIÈRE le rideau). Absent/false = déco d'arrière-plan classique (sous les entités). */
  front?: boolean
}

// Source de vérité unique (miroir de WORLDS pour les mondes). Les chars `ch` doivent être UNIQUES
// à travers TOUTES les couches (grille principale comprise : l'éditeur route le pinceau PAR CHAR).
// Le registre complet vit dans LEVEL_CHARS (shared/level.ts) ; l'unicité globale est garantie par
// tests/chars-registry.test.ts (règle née de la collision caisses/décos du 16/07).
export const DECOS: Record<DecoKind, DecoMeta> = {
  torche:     { name: 'Torche',    ch: 'f', code: 1,  animated: true,  biome: 'all' },
  plante:     { name: 'Plante',    ch: 'p', code: 2,  animated: false, biome: 'all' },
  banniere:   { name: 'Bannière',  ch: 'B', code: 3,  animated: false, biome: 'all' },
  cristal:    { name: 'Cristal',   ch: 'C', code: 4,  animated: false, biome: 'all' },
  tonneau:    { name: 'Tonneau',   ch: 'o', code: 5,  animated: false, biome: 'all' },
  crane:      { name: 'Crâne',     ch: 'K', code: 6,  animated: false, biome: 'all' },
  panneau:    { name: 'Panneau',   ch: 'P', code: 7,  animated: false, biome: 'all' },
  chaine:     { name: 'Chaîne',    ch: 'h', code: 8,  animated: false, biome: 'all' },
  cactus:     { name: 'Cactus',    ch: 'a', code: 9,  animated: false, biome: 'desert' },
  bonhomme:   { name: 'Bonhomme',  ch: '8', code: 10, animated: false, biome: 'neige' }, // '8' (2 boules) : 'w' recouvrait le tapis descendant -> le tapis posait un bonhomme (fix 16/07)
  valve:      { name: 'Valve',     ch: 'Y', code: 11, animated: true,  biome: 'usine' },
  champignon: { name: 'Champignon', ch: 'q', code: 12, animated: false, biome: 'grotte' },
  fleurs:     { name: 'Fleurs',    ch: 'e', code: 13, animated: false, biome: 'prairie' },
  toile:      { name: 'Toile',     ch: 'W', code: 14, animated: false, biome: 'classique' },
  bougie:     { name: 'Bougie',    ch: 'i', code: 15, animated: true,  biome: 'classique' },
  // Vague 2 (16/07, demande Claude-Alain) : socle universel élargi — 12 décos + 2 CASCADES de
  // premier plan (le coureur passe derrière le rideau semi-transparent). Codes 16+ = la feuille
  // continue ligne 11 (position dérivée du code : col (code-1)%16, ligne 10+floor((code-1)/16)).
  herbe:       { name: 'Herbe',            ch: 'g', code: 16, animated: false, biome: 'all' },
  buisson:     { name: 'Buisson',          ch: '4', code: 17, animated: false, biome: 'all' },
  rocher:      { name: 'Rocher',           ch: '0', code: 18, animated: false, biome: 'all' },
  souche:      { name: 'Souche',           ch: '6', code: 19, animated: false, biome: 'all' },
  lierre:      { name: 'Lierre',           ch: 'l', code: 20, animated: false, biome: 'all' },
  stalagmite:  { name: 'Stalagmite',       ch: '?', code: 21, animated: false, biome: 'all' }, // 'A' pris (trampoline puissant)
  pierres:     { name: 'Tas de pierres',   ch: '7', code: 22, animated: false, biome: 'all' },
  lanterne:    { name: 'Lanterne',         ch: '5', code: 23, animated: true,  biome: 'all' },
  fanion:      { name: 'Fanion',           ch: '9', code: 24, animated: true,  biome: 'all' },
  lucioles:    { name: 'Lucioles',         ch: '*', code: 25, animated: true,  biome: 'all' },
  barriere:    { name: 'Barrière',         ch: '%', code: 26, animated: false, biome: 'all' },
  fleche:      { name: 'Flèche en bois',   ch: '@', code: 27, animated: false, biome: 'all' },
  cascadeEau:  { name: "Cascade d'eau",    ch: '&', code: 28, animated: true,  biome: 'all', front: true }, // '~' pris (tuile EAU)
  cascadeLave: { name: 'Cascade de lave',  ch: '!', code: 29, animated: true,  biome: 'all', front: true },
  // Signatures des 2 mondes du 16/07 nuit. Codes 30/31 = les cases [13,11] et [14,11] que l'audit
  // avait LIBÉRÉES le soir même en déplaçant les rampes classiques (elles s'y trouvaient : la 30e
  // déco aurait volé leur sprite en silence). Le test croisé de sprites.test.ts garde la suite.
  // ⚠️ Chars : il ne reste QUE , ; [ ] ` ' dans tout l'ASCII lisible. Le corail visait '{' — la
  // session parallèle l'a pris pour son trampoline puissant directionnel PENDANT ce chantier (3e
  // collision de la journée, 3e fois attrapée par tests/chars-registry). Il est donc en '[' (une
  // branche), et la stèle garde '|' (une pierre debout) : tous deux distincts du '.' du vide quand
  // on lit une carte .txt à l'oeil.
  corail: { name: 'Corail',         ch: '[', code: 30, animated: false, biome: 'abysses' },
  tombe:  { name: 'Pierre tombale', ch: '|', code: 31, animated: false, biome: 'manoir' },
  // TRÉSORS (21/07, demande Claude-Alain : « garde-les en les ajoutant dans les décorations ») —
  // les 20 trésors de la planche /pages/tresors.html deviennent des décos d'éditeur (spécial salle
  // au trésor / map type Égypte). Codes 64+ (TRESOR_CODE_MIN) = 2e PLAGE de la feuille monde :
  // cellules ((code-64)%16, ligne 44+floor((code-64)/16)) AJOUTÉES sous les biomes HD (la 1re
  // plage, lignes 10-12, est prise par les tuiles). ⚠️ 40-63 = plage RÉSERVÉE aux ancres de
  // grands décors : les trésors y étaient nés (32-51) et 6 codes marchaient sur BIG_DECOS 40-45
  // (un Anneau posé dessinait un Arbre géant) — bug « mélange » du 21/07, verrouillé depuis par
  // tests/chars-registry (unicité croisée des codes + plages). Les cartes stockent des CHARS,
  // jamais des codes : le recodage 64+ a réparé les cartes existantes sans migration.
  // HD-only (aucune version 16 px). ⚠️ Chars : l'ASCII lisible est ÉPUISÉ (cf. note
  // corail/tombe) -> symboles latin-1, tous des unités UTF-16 simples SANS décomposition possible
  // (pas d'accents composables), donc sûrs dans les grilles sauvegardées. 'µ' évité (pris par la
  // grille principale, cf. LEVEL_CHARS). animated: étincelle qui pulse (drawDecoAnim).
  tresorDiamant:       { name: 'Diamant',         ch: '¢', code: 64, animated: true, biome: 'all' },
  tresorRubis:         { name: 'Rubis',           ch: '¡', code: 65, animated: true, biome: 'all' },
  tresorEmeraude:      { name: 'Émeraude',        ch: '¨', code: 66, animated: true, biome: 'all' },
  tresorSaphir:        { name: 'Saphir',          ch: '§', code: 67, animated: true, biome: 'all' },
  tresorAmethyste:     { name: 'Améthyste',       ch: '©', code: 68, animated: true, biome: 'all' },
  tresorCouronne:      { name: 'Couronne',        ch: '®', code: 69, animated: true, biome: 'all' },
  tresorSceptre:       { name: 'Sceptre',         ch: '¬', code: 70, animated: true, biome: 'all' },
  tresorCalice:        { name: 'Calice',          ch: '¯', code: 71, animated: true, biome: 'all' },
  tresorAnneau:        { name: 'Anneau',          ch: '´', code: 72, animated: true, biome: 'all' },
  tresorAmulette:      { name: 'Amulette',        ch: '²', code: 73, animated: true, biome: 'all' },
  tresorOeuf:          { name: 'Œuf impérial',    ch: '³', code: 74, animated: true, biome: 'all' },
  tresorLingot:        { name: 'Lingot',          ch: '¼', code: 75, animated: true, biome: 'all' },
  tresorSac:           { name: "Sac d'or",        ch: '½', code: 76, animated: true, biome: 'all' },
  tresorIdole:         { name: 'Idole dorée',     ch: '¾', code: 77, animated: true, biome: 'all' },
  tresorMasque:        { name: 'Masque antique',  ch: '¸', code: 78, animated: true, biome: 'all' },
  tresorScarabee:      { name: 'Scarabée doré',   ch: '¹', code: 79, animated: true, biome: 'all' },
  tresorPerle:         { name: 'Perle',           ch: 'º', code: 80, animated: true, biome: 'all' },
  tresorCristalPolaire: { name: 'Cristal polaire', ch: '·', code: 81, animated: true, biome: 'all' },
  tresorRouage:        { name: "Rouage d'or",     ch: '€', code: 82, animated: true, biome: 'all' },
  tresorGrimoire:      { name: 'Grimoire',        ch: '÷', code: 83, animated: true, biome: 'all' },
  // GRAVURES ÉGYPTIENNES (Pharaon premium 22/07) : décors MURAUX du désert, à poser sur les murs
  // de grès (l'auteur choisit ; la pyramide se raconte). Codes ≥ 64 = feuille monde lignes 44+
  // (HD nette obligatoire pour des gravures fines) mais animated: false — PAS l'étincelle des
  // trésors (le rendu la conditionne à `animated`, pas à la plage).
  hieroglyphes:        { name: 'Hiéroglyphes',    ch: 'ß', code: 84, animated: false, biome: 'desert' }, // 'µ' pris (moine bâtisseur)
  cartouche:           { name: 'Cartouche royal', ch: 'Æ', code: 85, animated: false, biome: 'desert' },
  oeilHorus:           { name: "Œil d'Horus",     ch: 'Ø', code: 86, animated: false, biome: 'desert' },
}

/** Premier code de la plage TRÉSORS (feuille monde, lignes 44+). Partagé rendu/feuille/tests. */
export const TRESOR_CODE_MIN = 64

/** Méta par code runtime (1..N) — lookup O(1) côté rendu (étincelle des trésors, etc.). */
export const DECO_BY_CODE: Readonly<Record<number, DecoMeta>> = Object.fromEntries(
  Object.values(DECOS).map((d) => [d.code, d]),
)

export type DecorGrid = Uint8Array
export const EMPTY_DECOR = ''

// ---- GRANDS DÉCORS (17/07, « ok pour tous ») : des ENSEMBLES multi-cases posés par une case
// ANCRE (même recette que le ver checkpoint), 100 % cosmétiques, DESSINÉS PROCÉDURALEMENT au
// rendu (drawBigDeco de render.ts — les maquettes du carnet sont devenues le code) : aucune
// case de feuille de sprites. Stockés dans la MÊME couche décor (un char/code par ancre) :
// toDecorString, decorGrid et le serveur suivent sans changement. Chars = second alphabet
// (symboles Latin-1 sûrs). ax/ay = position de l'ancre DANS l'emprise w×h (le dessin s'étend
// de (col-ax, row-ay) à (col-ax+w-1, row-ay+h-1)).
export type BigDecoKind = 'arbre' | 'epave' | 'baleine' | 'grue' | 'vitrail' | 'campement'
export interface BigDecoMeta { name: string; ch: string; code: number; w: number; h: number; ax: number; ay: number; biome: 'all' | WorldKey }
export const BIG_DECOS: Record<BigDecoKind, BigDecoMeta> = {
  arbre:     { name: 'Arbre géant',          ch: '¥', code: 40, w: 3, h: 4, ax: 1, ay: 3, biome: 'all' },
  epave:     { name: 'Épave de barque',      ch: '¿', code: 41, w: 4, h: 2, ax: 1, ay: 1, biome: 'abysses' }, // 'µ' cédé au moine bâtisseur (session parallèle, poussé avant) — '¿' : la coque renversée
  baleine:   { name: 'Squelette de baleine', ch: '×', code: 42, w: 6, h: 2, ax: 0, ay: 1, biome: 'all' },
  grue:      { name: 'Grue à crochet',       ch: '£', code: 43, w: 3, h: 4, ax: 0, ay: 3, biome: 'usine' },
  vitrail:   { name: 'Vitrail gothique',     ch: '¶', code: 44, w: 2, h: 3, ax: 0, ay: 2, biome: 'manoir' },
  campement: { name: 'Campement',            ch: '±', code: 45, w: 3, h: 2, ax: 1, ay: 1, biome: 'all' },
}
/** Grand décor d'un code runtime (ou undefined si le code n'est pas une ancre de grand décor). */
const CODE_TO_BIG = new Map<number, BigDecoKind>((Object.keys(BIG_DECOS) as BigDecoKind[]).map(k => [BIG_DECOS[k].code, k]))
export const bigOfCode = (code: number): BigDecoKind | undefined => CODE_TO_BIG.get(code)

// Feuille decors.png (gen-sprites.mjs, section GRANDS DÉCORS — 18/07) : une bande par décor
// dans CET ordre (hauteur h*16 chacune), les frames d'animation côte à côte (x = frame*w*16).
// tests/decors-sheet.test.ts verrouille la cohérence ordre/dimensions avec le PNG généré.
export const BIG_DECO_ORDER: BigDecoKind[] = ['arbre', 'epave', 'baleine', 'grue', 'vitrail', 'campement']
export const BIG_FRAMES = 3

const ALL_DECO_CHARS: [string, number][] = [
  ...Object.values(DECOS).map(d => [d.ch, d.code] as [string, number]),
  ...Object.values(BIG_DECOS).map(d => [d.ch, d.code] as [string, number]),
]
const CH_TO_CODE = new Map<string, number>(ALL_DECO_CHARS)
const CODE_TO_CH = new Map<number, string>(ALL_DECO_CHARS.map(([ch, code]) => [code, ch]))
const CODE_TO_KIND = new Map<number, DecoKind>((Object.keys(DECOS) as DecoKind[]).map(k => [DECOS[k].code, k]))

/** Garde de type : `x` est-il un type de déco connu ? */
export function isDecoKind(x: unknown): x is DecoKind {
  return typeof x === 'string' && Object.prototype.hasOwnProperty.call(DECOS, x)
}

/**
 * Normalise une valeur en grille déco VALIDE (chars connus, sinon `.`), ou `''` (vide) si absente /
 * non rectangulaire / hors bornes / entièrement vide après nettoyage. Lot E : les DIMENSIONS sont
 * DÉDUITES de la chaîne (comme `levelFromAscii`), pas figées à 28×16 — la déco suit la taille de la
 * carte (jusqu'à MAX_COLS×MAX_ROWS). Frontière DB/API/protocole : jamais de déco corrompue. Miroir de `toWorld`.
 */
export function toDecorString(x: unknown): string {
  if (typeof x !== 'string' || x === '') return EMPTY_DECOR
  const rows = x.split('\n')
  const rowN = rows.length, colN = rows[0]!.length
  if (rowN === 0 || colN === 0 || rowN > MAX_ROWS || colN > MAX_COLS) return EMPTY_DECOR
  const clean: string[] = []
  for (const line of rows) {
    if (line.length !== colN) return EMPTY_DECOR   // grille non rectangulaire -> rejetée
    let out = ''
    for (const ch of line) out += CH_TO_CODE.has(ch) ? ch : '.'   // char inconnu -> case vide
    clean.push(out)
  }
  const emptyLine = '.'.repeat(colN)
  return clean.every(l => l === emptyLine) ? EMPTY_DECOR : clean.join('\n')  // tout vide -> ''
}

/** Parse la chaîne en Uint8Array de codes (0 = vide), aux DIMENSIONS déduites de la chaîne (Lot E).
 *  Chaîne vide -> tableau vide (aucune déco). Pour le rendu (render() itère decor.length + colOf(cell, cur.cols)). */
export function decorGrid(decorStr: string): DecorGrid {
  if (!decorStr) return new Uint8Array(0)
  const rows = decorStr.split('\n')
  const rowN = rows.length, colN = rows[0]!.length
  const g = new Uint8Array(rowN * colN)
  for (let r = 0; r < rowN; r++) {
    const line = rows[r]!
    for (let c = 0; c < colN && c < line.length; c++) g[r * colN + c] = CH_TO_CODE.get(line[c]!) ?? 0
  }
  return g
}

/** Caractère d'éditeur d'un code (ou `.` si inconnu / 0). */
export const codeToChar = (code: number): string => CODE_TO_CH.get(code) ?? '.'
/** Type de déco d'un code (ou undefined). */
export const kindOfCode = (code: number): DecoKind | undefined => CODE_TO_KIND.get(code)
